import React, { useCallback, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputField,
  Typography,
  makeStyles
} from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import ClearIcon from '@material-ui/icons/Clear'
import { useClientOptions, useUser } from '@paintscout/ui-business'
import { hasIntegrationInterface } from '@paintscout/util/builder'
import { useApolloClient } from '@apollo/react-hooks'
import { AppDocsDocument, useOutstandingInvoiceQuery, usePayOutstandingInvoiceMutation } from '@paintscout/api'
import { useAuth } from 'react-auth0'
import { Sentry } from 'src/util/appSentry'
import {
  CONTACT_SUPPORT_LABEL,
  PAYMENT_FAILURE_DIALOG_TITLE,
  PAYMENT_FAILURE_HEADING,
  PAY_NOW_LABEL,
  getChargeNotice,
  getCustomerNote,
  getPaymentFailureBody
} from 'src/util/dunningCopy'
import ChargebeeCardFields from './ChargebeeCardFields'
import DunningCopyText from './DunningCopyText'
import type { ChargebeeCardFieldsHandle } from './ChargebeeCardFields'

const useStyles = makeStyles<Theme>(
  (theme) => ({
    // A solid brand-blue title bar with plain white 16px text, per the Figma (node 2396:464).
    //
    // `&&` doubles the class so these beat DialogTitle's own withStyles rules, which are a single
    // class of equal specificity and win or lose on stylesheet injection order alone. DialogTitle
    // otherwise applies `theme.typography.h3` and a grey bottom border, which is what left this
    // bar looking like every other dialog's.
    title: {
      '&&': {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
        // Figma: 18.926 top / 17.185 bottom / 16 sides.
        padding: '19px 16px 17px',
        borderBottom: 'none',
        borderTopLeftRadius: 5,
        borderTopRightRadius: 5,
        fontSize: 16,
        fontWeight: 400,
        lineHeight: 'normal'
      }
    },
    closeButton: {
      color: theme.palette.common.white
    },
    content: {
      paddingTop: theme.spacing(3)
    },
    heading: {
      // Figma: Inter Semi Bold 30 / 1.4.
      fontSize: 30,
      fontWeight: 600,
      lineHeight: 1.4,
      // Figma says #3d4146, which is not a project token. The app's own body colour (Neutral.P40,
      // #394860) is within a hair of it, so this stays on the token rather than introducing a
      // one-off hex that would drift from every other screen.
      marginBottom: theme.spacing(2)
    },
    body: {
      marginBottom: theme.spacing(2)
    },
    nameRow: {
      marginBottom: theme.spacing(2)
    },
    chargeNotice: {
      marginTop: theme.spacing(1)
    },
    // Contact Support sits hard left, Pay Now hard right. MUI's DialogActions right-aligns and
    // gap-spaces its children by default, so both have to be overridden through `classes` — a
    // className alone loses to MUI's own rule.
    actions: {
      justifyContent: 'space-between',
      // Figma puts a full-width rule above the footer row.
      borderTop: `1px solid ${theme.palette.grey[300]}`,
      padding: theme.spacing(2),
      '& > :not(:first-child)': {
        marginLeft: 0
      }
    },
    supportButton: {
      // A quiet grey link, not a second call to action competing with Pay Now.
      fontWeight: 500,
      color: theme.paintscout.palette.brand.Neutral.P50
    },
    dialogPaper: {
      border: 'none'
    },
    errorAlert: {
      marginTop: theme.spacing(2)
    }
  }),
  { name: 'PaymentFailureDialog' }
)

/**
 * Strips Apollo's `GraphQL error: ` prefix so a message written for a customer reads like one.
 *
 * apollo-client 2.x builds `ApolloError.message` by prefixing every GraphQL error it carries, and
 * that string is what surfaces on any message this dialog passes through rather than authors.
 */
function toDisplayMessage(message?: string | null): string | undefined {
  return message?.replace(/^GraphQL error:\s*/i, '').trim() || undefined
}

export interface PaymentFailureDialogProps {
  /** Supplied by the shared dialog stack, which owns this dialog's visibility. */
  open?: boolean
  /** Skip the explainer and land on the card form — what the header banner's Pay Now does. */
  startOnCardForm?: boolean
  onClose: () => void
}

/**
 * The failed-payment notice (ENG-1532), in two steps: the explainer, then the card form.
 *
 * Paying here does two things at once — attaches the card AND collects the outstanding invoice —
 * so the banner clears while the user is watching rather than on Chargebee's next retry.
 *
 * A declined card is an expected outcome, not an edge case: this account is here precisely because
 * a card already failed. The mutation returns the gateway's message instead of throwing, and it is
 * rendered inline so the user can try another card without losing the dialog.
 */
export default function PaymentFailureDialog({ open, startOnCardForm, onClose }: PaymentFailureDialogProps) {
  const classes = useStyles()
  const { dunning, user, preferences } = useUser()
  const { options } = useClientOptions()
  const { authenticate } = useAuth()
  const apolloClient = useApolloClient()
  const cardRef = useRef<ChargebeeCardFieldsHandle>(null)

  // Only promise "make payments" when the account actually has a processor connected. The same
  // check the payments UI uses to decide whether a customer can pay at all.
  // Memoized: this sits above the name-field state, so without it every keystroke re-runs three
  // option-tree walks.
  const hasPayments = useMemo(
    () =>
      hasIntegrationInterface({ preferences, options, integration: 'stripe' }) ||
      hasIntegrationInterface({ preferences, options, integration: 'stripeW1d' }) ||
      hasIntegrationInterface({ preferences, options, integration: 'payrix' }),
    [preferences, options]
  )

  const [showCardForm, setShowCardForm] = useState(Boolean(startOnCardForm))
  const [firstName, setFirstName] = useState(user?.user_metadata?.firstName ?? '')
  const [lastName, setLastName] = useState(user?.user_metadata?.lastName ?? '')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  // Live from Chargebee rather than the meta document: this is a payment screen, and a stale
  // amount here would promise a charge we do not make. Skipped until the card form is on screen,
  // so the explainer step costs no Chargebee call.
  const { data } = useOutstandingInvoiceQuery({ skip: !open || !showCardForm, fetchPolicy: 'network-only' })
  const [payOutstandingInvoice] = usePayOutstandingInvoiceMutation()

  const invoice = data?.outstandingInvoice
  const amountDue = invoice?.amountDue ?? dunning?.amountDue
  const currency = invoice?.currency ?? dunning?.currency
  const deactivationAt = invoice?.deactivationAt ?? dunning?.deactivationAt

  // Opens the HubSpot chat widget rather than an email client. HubspotChat listens for this event
  // app-wide and loads its own bundle on demand — the same handoff GitBookDocs uses for its
  // Contact Support action. The dialog closes first, or the chat panel opens behind it.
  const handleContactSupport = useCallback(() => {
    onClose()
    window.dispatchEvent(new Event('ps:open-hubspot-chat'))
  }, [onClose])

  const handleSubmit = useCallback(async () => {
    setError(null)
    setSubmitting(true)

    try {
      let tempToken: string | undefined

      // Tokenization runs in its own try: every message it can throw is one this file authored
      // (fields incomplete, form still loading), so those are safe to show verbatim.
      try {
        // The cardholder name belongs on the payment source Chargebee creates from this token.
        tempToken = await cardRef.current?.tokenize({ firstName, lastName })
      } catch (tokenizeError) {
        setError(
          (tokenizeError instanceof Error ? toDisplayMessage(tokenizeError.message) : undefined) ??
            'Please check the card details and try again.'
        )
        return
      }

      if (!tempToken) {
        setError('Please check the card details and try again.')
        return
      }

      let payload
      // The mutation is a different matter. A thrown error here is a server fault, and its
      // message is whatever Apollo made of it — including, once, Chargebee's entire JSON body
      // rendered inside this dialog. Only `payload.message` is written for a human to read.
      try {
        const result = await payOutstandingInvoice({ variables: { tempToken, firstName, lastName } })
        payload = result?.data?.payOutstandingInvoice
      } catch (mutationError) {
        setError('We could not process that payment. Please try again, or contact support.')
        Sentry.captureException(mutationError)
        return
      }

      if (!payload?.success) {
        setError(toDisplayMessage(payload?.message) ?? 'That payment did not go through. Please try another card.')
        return
      }

      // Clear the banner without a reload. The token carries `dunning.status` and appDocs carries
      // the rest, so both have to be refreshed — refetching only one leaves the banner up.
      await Promise.all([
        authenticate({ ignoreCache: true }).catch(() => {}),
        apolloClient.query({ query: AppDocsDocument, fetchPolicy: 'network-only' }).catch(() => {})
      ])

      onClose()
    } catch (err) {
      setError('Something went wrong taking that payment. Please try again.')
      Sentry.captureException(err)
    } finally {
      setSubmitting(false)
    }
  }, [payOutstandingInvoice, firstName, lastName, authenticate, apolloClient, onClose])

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" classes={{ paper: classes.dialogPaper }}>
      <DialogTitle
        classes={{ root: classes.title }}
        rightContent={
          <IconButton size="small" className={classes.closeButton} style={{ padding: 0 }} onClick={onClose}>
            <ClearIcon />
          </IconButton>
        }
      >
        {PAYMENT_FAILURE_DIALOG_TITLE}
      </DialogTitle>
      <DialogContent classes={{ root: classes.content }}>
        {!showCardForm ? (
          <>
            <Typography component="h2" className={classes.heading}>
              {PAYMENT_FAILURE_HEADING}
            </Typography>
            <Typography variant="body1" className={classes.body}>
              <DunningCopyText segments={getPaymentFailureBody(deactivationAt)} />
            </Typography>
            {/* The homeowner side never stops. Saying so up front is the point of the issue. */}
            <Alert severity="info" content={getCustomerNote({ options, hasPayments })} />
          </>
        ) : (
          <>
            <Grid container spacing={2} className={classes.nameRow}>
              <Grid item xs={12} sm={6}>
                <InputField
                  label="First Name"
                  value={firstName}
                  fullWidth
                  onChange={(ev: React.ChangeEvent<HTMLInputElement>) => setFirstName(ev.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InputField
                  label="Last Name"
                  value={lastName}
                  fullWidth
                  onChange={(ev: React.ChangeEvent<HTMLInputElement>) => setLastName(ev.target.value)}
                />
              </Grid>
            </Grid>

            <ChargebeeCardFields ref={cardRef} />

            <Typography variant="body2" className={classes.chargeNotice}>
              <DunningCopyText segments={getChargeNotice(amountDue, currency)} />
            </Typography>

            {error && <Alert severity="error" content={error} className={classes.errorAlert} />}
          </>
        )}
      </DialogContent>
      <DialogActions
        classes={{ root: classes.actions }}
        leftButton={
          <Button variant="text" className={classes.supportButton} onClick={handleContactSupport}>
            {CONTACT_SUPPORT_LABEL}
          </Button>
        }
      >
        <Button
          variant="contained"
          loading={submitting}
          onClick={showCardForm ? handleSubmit : () => setShowCardForm(true)}
        >
          {PAY_NOW_LABEL}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
